/**
 * Money, written one way on every screen: `150 000 so‘m`.
 *
 * The services send `{ amount, currency }` with `amount` in minor units
 * (5 000 000 UZS minor units are 50 000 so‘m). Thousands are separated by a
 * space that does not break, there is no fraction unless the sum has one, and
 * a negative sum takes a real minus sign (U+2212), not a hyphen.
 */
import type { Language } from "@/shared/i18n/languages";

export interface MoneyValue {
  /** Whole minor units. */
  amount: number;
  /** ISO 4217. */
  currency: string;
}

export const NBSP = " ";
export const MINUS = "−";

/** What a currency is called after a sum. A currency not listed shows its code. */
const CURRENCY_NAMES: Readonly<Record<string, Readonly<Record<Language, string>>>> = {
  UZS: { uz: "so‘m", ru: "сум", en: "UZS" },
  KZT: { uz: "tenge", ru: "тенге", en: "KZT" },
};

const DECIMAL_MARK: Readonly<Record<Language, string>> = { uz: ",", ru: ",", en: "." };

const minorDigitsCache = new Map<string, number>();

/** How many digits a currency's minor unit has (UZS: 2, JPY: 0), by ISO 4217. */
export function minorDigits(currency: string): number {
  const known = minorDigitsCache.get(currency);
  if (known !== undefined) return known;
  let digits = 2;
  try {
    digits =
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    // Not a currency code Intl knows: the services' standard is two.
  }
  minorDigitsCache.set(currency, digits);
  return digits;
}

/** `1234567` to `1 234 567`. */
export function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

export function currencyName(currency: string, language: Language): string {
  return CURRENCY_NAMES[currency]?.[language] ?? currency;
}

/**
 * A number with a fraction, the mark the language writes: `4,9` in Uzbek and
 * Russian, `4.9` in English. Up to `digits` places, at least one.
 */
export function formatDecimal(value: number, language: Language, digits = 2): string {
  const fixed = value.toFixed(digits).replace(/(\.\d+?)0+$/, "$1");
  return fixed.replace(".", DECIMAL_MARK[language]);
}

/** Basis points as the share a person types back, without its sign: 2000 is `20`, 1250 is `12,5`. */
export function shareText(basisPoints: number, language: Language): string {
  const percent = basisPoints / 100;
  return Number.isInteger(percent) ? String(percent) : formatDecimal(percent, language);
}

/** Basis points as a share: 2000 is `20%`, 1250 is `12,5%`. */
export function formatPercent(basisPoints: number, language: Language): string {
  const percent = basisPoints / 100;
  return `${Number.isInteger(percent) ? String(percent) : formatDecimal(percent, language)}%`;
}

/** The number alone: `150 000`, `−25 000`, `1 000,50`. */
export function formatAmount(money: MoneyValue, language: Language): string {
  const digits = minorDigits(money.currency);
  const scale = 10 ** digits;
  const minor = Math.round(Math.abs(money.amount));
  const whole = Math.trunc(minor / scale);
  const fraction = minor % scale;
  const sign = money.amount < 0 && minor !== 0 ? MINUS : "";
  const tail =
    fraction === 0 ? "" : `${DECIMAL_MARK[language]}${String(fraction).padStart(digits, "0")}`;
  return `${sign}${groupThousands(String(whole))}${tail}`;
}

/** `150 000 so‘m`. */
export function formatMoney(money: MoneyValue, language: Language): string {
  return `${formatAmount(money, language)}${NBSP}${currencyName(money.currency, language)}`;
}

/**
 * A sum as typed (`50 000`, `50000,5`) in the currency's minor units, by the
 * number of digits the market's currency keeps after the point. Null for
 * anything that is not a sum, for more digits after the point than the
 * currency has, and for a sum too large to be exact.
 */
export function parseAmount(text: string, exponent: number): number | null {
  const compact = text.replace(/\s/g, "");
  const match = /^(\d+)(?:[.,](\d+))?$/.exec(compact);
  if (match === null) return null;
  const whole = match[1] ?? "";
  const fraction = match[2] ?? "";
  if (fraction.length > exponent) return null;
  const minor = Number(whole + fraction.padEnd(exponent, "0"));
  return Number.isSafeInteger(minor) ? minor : null;
}

/**
 * A share as typed in percent (`20`, `12,5`) in basis points: 20% is 2000,
 * 12.5% is 1250. Null for what is not a share between 0 and 100, and for
 * more than two digits after the point.
 */
export function parsePercent(text: string): number | null {
  const match = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(text.replace(/\s|%/g, ""));
  if (match === null) return null;
  const points = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return points <= 10_000 ? points : null;
}
